import { useSession } from "@otto/plattform";
import { hasAssessment, predecessorOf } from "../lib/settings.js";
import { useCourseSwitches, type ClassCourse } from "../lib/data.js";
import type { CourseOption } from "../lib/assign.js";
import own from "./courses.module.css";
import styles from "../../../shared/components/shell.module.css";

/**
 * Was die Klasse an einem Lehrgang selbst entscheidet.
 *
 * Die Redaktion schreibt den Testtext und sagt, worauf ein Lehrgang aufbaut –
 * ob daraus eine Pflicht wird, steht hier. Der Inhalt ist die Möglichkeit,
 * dieser Abschnitt ist der Gebrauch.
 *
 * Sichtbar ist nur, was Wirkung hat: kein Test in der zugeordneten Fassung,
 * kein Haken; kein Vorgänger, kein Haken. Ein Schalter ohne Folgen ist eine
 * Frage ohne Antwort – und der Grund, warum jemand später den Test sucht, den
 * er angeblich eingeschaltet hat.
 *
 * Die Reihenfolge der Lektionen steht immer da: Lektionen hat jeder Lehrgang.
 */
/**
 * Regel und Schwelle als EIN Auswahlwert.
 *
 * Zwei Felder – „der Reihe nach“ und daneben eine Sternzahl – ließen den
 * Zustand „Schwelle 2, aber frei wählbar“ darstellbar werden. Es gibt ihn
 * nicht, und was es nicht gibt, soll man nicht auswählen können.
 */
function gateToValue(course: ClassCourse): string {
  if (course.lessonGate !== "reihenfolge") return course.lessonGate;
  return course.lessonGateStars === null
    ? "reihenfolge"
    : `reihenfolge:${course.lessonGateStars}`;
}

function valueToGate(value: string): { gate: string; stars: number | null } {
  const [gate, stars] = value.split(":");
  return { gate: gate ?? "frei", stars: stars === undefined ? null : Number(stars) };
}

export function ClassCourseSettings({
  classId,
  course,
  catalog,
}: {
  classId: string;
  course: ClassCourse;
  catalog: readonly CourseOption[];
}) {
  const { client } = useSession();
  const switches = useCourseSwitches(client, classId);

  const showAssessment = hasAssessment(course);
  const predecessor = predecessorOf(course, catalog);

  const testId = `test-${course.curriculumId}`;
  const requiresId = `vorgaenger-${course.curriculumId}`;
  const gateId = `lektionen-${course.curriculumId}`;
  const gateValue = gateToValue(course);
  const schwelle = course.lessonGate === "reihenfolge" && course.lessonGateStars !== null;

  return (
    <section className={own.section}>
      <h4 className={own.sectionTitle}>Einstellungen dieser Klasse</h4>

      {showAssessment && (
        <>
          <div className={own.switch}>
            <input
              id={testId}
              type="checkbox"
              checked={course.assessmentEnabled}
              disabled={switches.isPending}
              onChange={(event) =>
                switches.mutate({
                  curriculumId: course.curriculumId,
                  assessmentEnabled: event.target.checked,
                })
              }
            />
            <label htmlFor={testId}>Eingangs- und Abschlusstest verwenden</label>
          </div>
          <p className={`${styles.subtitle} ${own.hint}`}>
            Die Kinder tippen denselben kurzen Text einmal vor und einmal nach dem
            Lehrgang. Ohne Haken entfällt der Test; bereits gemessene Werte bleiben in den
            Ergebnissen stehen.
          </p>
        </>
      )}

      {predecessor && (
        <>
          <div className={own.switch}>
            <input
              id={requiresId}
              type="checkbox"
              checked={course.requiresEnforced}
              disabled={switches.isPending}
              onChange={(event) =>
                switches.mutate({
                  curriculumId: course.curriculumId,
                  requiresEnforced: event.target.checked,
                })
              }
            />
            {/* Namen aus den Daten, nicht im Text: "sechs Lektionen" stand in der
                Lern-App noch, als der Lehrgang längst acht hatte. */}
            <label htmlFor={requiresId}>
              {course.title} erst nach {predecessor.title}
            </label>
          </div>
          <p className={`${styles.subtitle} ${own.hint}`}>
            Ohne Haken können die Kinder sofort mit {course.title} beginnen. Am Lehrgang
            selbst ändert das nichts – {predecessor.title} bleibt sein inhaltlicher
            Vorgänger.
          </p>
        </>
      )}

      <div className={styles.spaced}>
        <label htmlFor={gateId}>Reihenfolge der Lektionen</label>
        <select
          id={gateId}
          className={`${styles.field} ${own.narrow}`}
          value={gateValue}
          disabled={switches.isPending || gateValue === "freigabe"}
          onChange={(event) =>
            switches.mutate({
              curriculumId: course.curriculumId,
              lessonGate: valueToGate(event.target.value),
            })
          }
        >
          <option value="frei">Die Kinder wählen frei</option>
          <option value="reihenfolge">
            Der Reihe nach – eine Lektion öffnet, wenn die vorige geübt ist
          </option>
          <option value="reihenfolge:1">Der Reihe nach, mit mindestens 1 Stern</option>
          <option value="reihenfolge:2">Der Reihe nach, mit mindestens 2 Sternen</option>
          <option value="reihenfolge:3">Der Reihe nach, mit mindestens 3 Sternen</option>
          {/* Nur, wenn dieser Wert tatsächlich gesetzt ist. Die Ausbaustufe ist
              zurückgestellt; die Datenbank lässt den Wert schon zu, und ein Feld,
              das einen gesetzten Wert nicht anzeigen kann, zeigt einen falschen. */}
          {gateValue === "freigabe" && (
            <option value="freigabe">Freigabe je Kind (noch nicht eingerichtet)</option>
          )}
        </select>

        {schwelle && (
          <p className={`${styles.subtitle} ${own.hint}`} style={{ marginLeft: 0 }}>
            Ein Kind, dem eine Zeile nicht gelingt, kommt damit nicht weiter. Sie sehen in
            den Ergebnissen, wo es hakt, und können die Schwelle hier jederzeit
            zurücknehmen.
          </p>
        )}
      </div>

      {switches.isError && (
        <div className={`${styles.notice} ${styles.noticeError} ${styles.spaced}`}>
          Die Einstellung konnte nicht gespeichert werden.
        </div>
      )}
    </section>
  );
}
